/**
 * Formulário público QACI / Percepção de Riscos — página standalone (rota
 * `/q/:token`, sem autenticação). Orquestra a carga dos metadados do ciclo, os
 * estados de borda (carregando/inexistente/encerrado/erro) e o envio; delega a
 * experiência para a casca desktop ou mobile conforme o dispositivo.
 */
import type { FormularioPublico, ImgrRespostaDTO } from "@caint/shared";
import { useCallback, useEffect, useState } from "react";
import { ApiError, apiClient } from "../lib/api.ts";
import { useVersao } from "../lib/dispositivo.ts";
import { PercepcaoDesktop } from "./PercepcaoDesktop.tsx";
import { PercepcaoMobile } from "./PercepcaoMobile.tsx";
import type { ResultadoEnvio } from "./percepcao.tsx";
import "./publico.css";

type Fase = "carregando" | "pronto" | "inexistente" | "fechado" | "erro";

function formatarPrazo(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  try {
    return new Intl.DateTimeFormat("pt-BR", { dateStyle: "long" }).format(new Date(iso));
  } catch {
    return null;
  }
}

/** Telas de estado (carregando / inexistente / encerrado / erro). */
function Estado({ icone, titulo, texto }: { icone: string; titulo: string; texto: string }) {
  return (
    <div className="pub-estado">
      <div className="pub-estado-card">
        <div className="pub-estado-icon">{icone}</div>
        <h1>{titulo}</h1>
        <p>{texto}</p>
      </div>
    </div>
  );
}

export function FormularioImgr({ token }: { token: string }) {
  const [fase, setFase] = useState<Fase>("carregando");
  const [meta, setMeta] = useState<FormularioPublico | null>(null);
  const [enviando, setEnviando] = useState(false);
  const versao = useVersao();

  useEffect(() => {
    let vivo = true;
    apiClient
      .formularioPublico(token)
      .then((m) => {
        if (!vivo) {
          return;
        }
        setMeta(m);
        setFase(m.aberto ? "pronto" : "fechado");
      })
      .catch((e) => {
        if (!vivo) {
          return;
        }
        setFase(e instanceof ApiError && e.status === 404 ? "inexistente" : "erro");
      });
    return () => {
      vivo = false;
    };
  }, [token]);

  const enviar = useCallback(
    async (dto: ImgrRespostaDTO): Promise<ResultadoEnvio> => {
      setEnviando(true);
      try {
        await apiClient.enviarImgr(token, dto);
        return "ok";
      } catch (e) {
        if (e instanceof ApiError && e.status === 410) {
          setFase("fechado");
          return "fechado";
        }
        return { erro: e instanceof Error ? e.message : "Falha ao enviar. Tente novamente." };
      } finally {
        setEnviando(false);
      }
    },
    [token],
  );

  if (fase === "carregando") {
    return (
      <div className="pub-estado">
        <div className="pub-estado-card">
          <div className="pub-spinner" />
          <p style={{ marginTop: 14 }}>Carregando o questionário…</p>
        </div>
      </div>
    );
  }
  if (fase === "inexistente") {
    return (
      <Estado
        icone="🔎"
        titulo="Link não encontrado"
        texto="Verifique o endereço ou o QR Code com a sua coordenação."
      />
    );
  }
  if (fase === "erro") {
    return (
      <Estado
        icone="⚠️"
        titulo="Não foi possível abrir"
        texto="Houve um erro de conexão. Tente novamente em instantes."
      />
    );
  }
  if (fase === "fechado") {
    const prazo = formatarPrazo(meta?.prazo ?? null);
    return (
      <Estado
        icone="⏳"
        titulo="Formulário encerrado"
        texto={`O prazo de resposta${prazo ? ` (${prazo})` : ""} já se encerrou. Obrigado pelo interesse.`}
      />
    );
  }
  if (!meta) {
    return null;
  }

  return versao === "desktop" ? (
    <PercepcaoDesktop meta={meta} enviar={enviar} enviando={enviando} />
  ) : (
    <PercepcaoMobile meta={meta} enviar={enviar} enviando={enviando} />
  );
}
